import React from "react";
import { Form } from "react-bootstrap";
import styles from "./FormField.module.scss";
import clsx from "clsx";
// Import icons from react-icons
import { FaLock } from "react-icons/fa";
import { FaCircleExclamation } from "react-icons/fa6";

interface FormFieldProps {
  label: string;
  helpText?: string;
  name: string;
  type?: string;
  placeholder?: string;
  value?: string;
  error?: boolean;
  errorMessage?: string;
  as?: React.ElementType;
  children?: React.ReactNode;
  disabled?: boolean;
  validated?: boolean;
  variant?: "default" | "horizontal";
  onChange?: (event: React.ChangeEvent<any>) => void;
  className?: string;
  minDate?: string;
  maxDate?: string;
  autoComplete?: string;
  // New props for right section icon
  rightSectionIcon?: React.ReactNode;
  hideDefaultRightIcon?: boolean;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  helpText,
  name,
  type = "text",
  placeholder,
  value,
  error = false,
  errorMessage,
  as = "input",
  children = null,
  disabled = false,
  validated = false,
  variant = "default",
  onChange,
  className,
  minDate,
  maxDate,
  autoComplete,
  // Add new props to destructuring
  rightSectionIcon,
  hideDefaultRightIcon = false,
}) => {
  // Determine which icon to show in the right section
  const renderRightIcon = () => {
    // If custom icon is provided, use it
    if (rightSectionIcon) {
      return rightSectionIcon;
    }

    // If not hiding default icons and input is disabled, show locked icon
    if (!hideDefaultRightIcon && disabled) {
      return <FaLock className={styles.lockIcon} />;
    }

    // If not hiding default icons and input has error and is validated, show error icon
    if (!hideDefaultRightIcon && error && validated) {
      return <FaCircleExclamation className={styles.errorIcon} />;
    }

    return null;
  };

  const hasRightIcon =
    rightSectionIcon ||
    (!hideDefaultRightIcon && (disabled || (error && validated)));

  return (
    <Form.Group
      className={clsx(
        styles.formGroup,
        variant === "horizontal" && styles.horizontal,
        className
      )}
    >
      <Form.Label data-variant="label-2-bold">{label}</Form.Label>
      {helpText && <Form.Text className="text-muted">{helpText}</Form.Text>}
      <div className={styles.inputContainer}>
        {children || (
          <>
            <div
              className={clsx(
                styles.controlWrapper,
                hasRightIcon && styles.hasRightIcon
              )}
            >
              <Form.Control
                size="lg"
                name={name}
                type={type}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                disabled={disabled}
                isInvalid={error && validated}
                isValid={!error && validated}
                as={as}
                min={type === "date" ? minDate : undefined}
                max={type === "date" ? maxDate : undefined}
                autoComplete={autoComplete}
                className={hasRightIcon ? styles.inputWithRightIcon : ""}
              />
              {hasRightIcon && (
                <div className={styles.rightIconSection}>
                  {renderRightIcon()}
                </div>
              )}
            </div>
            {error && validated && (
              <Form.Control.Feedback type="invalid">
                {errorMessage || `Invalid ${label.toLowerCase()}`}
              </Form.Control.Feedback>
            )}
          </>
        )}
      </div>
    </Form.Group>
  );
};

export default FormField;
